import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { BzButton } from '../button/bz-button';
import { BzInput } from '../input/bz-input';
import { BzDialogLayout } from './bz-dialog-layout';
import { BZ_DIALOG_DATA, BzDialogRef } from './bz-dialog-ref';

export interface BzPromptDialogData {
  title: string;
  /** Optional explanatory line above the field. */
  message?: string;
  label?: string;
  placeholder?: string;
  /** Pre-filled value. */
  value?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Blocks confirm while the field is empty. Default true. */
  required?: boolean;
}

/**
 * The body behind `BzDialogService.prompt()` — ask for one string.
 *
 * This shape is worth a canned dialog because the console has several of it
 * already (a note, an external id, a password), each with its own template and
 * controller doing the same three things: show a field, validate non-empty,
 * resolve the value.
 */
@Component({
  selector: 'bz-prompt-dialog',
  imports: [BzDialogLayout, BzButton, BzInput],
  templateUrl: './bz-prompt-dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BzPromptDialog {
  protected readonly data = inject<BzPromptDialogData>(BZ_DIALOG_DATA);
  private readonly ref = inject(BzDialogRef<string | null>);

  protected readonly value = signal(this.data.value ?? '');

  protected get canConfirm(): boolean {
    return this.data.required === false || this.value().trim().length > 0;
  }

  /** Cancelling resolves null, which reads differently from an empty string. */
  protected cancel(): void {
    this.ref.close(null);
  }

  protected confirm(): void {
    if (this.canConfirm) this.ref.close(this.value());
  }
}
